// utils/timezone.ts
import { format } from "date-fns";
import { fromZonedTime, toZonedTime } from "date-fns-tz";

// Calendar / booking helpers treat wall-clock times as the selected shop's zone.
// Falls back to the browser zone until a shop is selected.
let activeShopTimeZone: string | null = null;

export const setActiveShopTimeZone = (timeZoneId?: string | null) => {
    activeShopTimeZone = timeZoneId || null;
};

export const shopTimeZoneFromShop = (shop?: { timezone?: { timeZoneId?: string; time_zone_id?: string } } | null) => {
    return shop?.timezone?.timeZoneId || shop?.timezone?.time_zone_id || null;
};

export const shopTimeZoneFromShops = (shops?: any[], shopId?: number | null) => {
    const id = shopId ?? Number(new URLSearchParams(window.location.search).get("shopId"));
    const list = shops || [];
    const shop = list.find((s: any) => s.id === Number(id)) || list[0];
    return shopTimeZoneFromShop(shop) || activeShopTimeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
};

export const getUserTimezone = (shop?: { timezone?: { timeZoneId?: string } } | null) => {
    return shopTimeZoneFromShop(shop) || activeShopTimeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
};

export const toLocalDate = (utcString: string, timeZoneId?: string | null) => {
    const timezone = timeZoneId || getUserTimezone();
    const zoned = toZonedTime(utcString, timezone);
    return new Date(
        zoned.getFullYear(),
        zoned.getMonth(),
        zoned.getDate(),
        zoned.getHours(),
        zoned.getMinutes(),
        zoned.getSeconds()
    );
};
// "Now" as the shop's wall clock (the same convention as toLocalDate): the calendar's today, its TODAY
// button and its now-line. new Date() is the viewer's clock - from India it's already the shop's tomorrow.
export const shopNow = (timeZoneId?: string | null) => toLocalDate(new Date().toISOString(), timeZoneId);

export const formatLocalTime = (utcString: string, timeZoneId?: string | null) => {
    const timezone = timeZoneId || getUserTimezone();
    return format(toZonedTime(utcString, timezone), "hh:mm a");
};

export const formatLocalDate = (utcString: string, timeZoneId?: string | null) => {
    const timezone = timeZoneId || getUserTimezone();
    return format(toZonedTime(utcString, timezone), "yyyy-MM-dd");
};

/**
 * Converts a Date constructed by toLocalDate() (whose fields represent shop-timezone wall-clock)
 * back to a proper UTC ISO string suitable for API payloads.
 *
 * Use whenever you need to send a time to the backend that was
 * previously converted with toLocalDate().
 */
export const localDateToUtc = (localDate: Date, timeZoneId?: string | null): string => {
    const timezone = timeZoneId || getUserTimezone();
    // Treat the local Date's fields as shop-timezone time → convert to true UTC
    return fromZonedTime(localDate, timezone).toISOString();
};

// Schedule APIs expect the selected calendar day as YYYY-MM-DD in the shop zone.
// Sending a Date ISO instant (local midnight) makes Chicago/Pacific land on the previous day.
export const toCalendarDateParam = (date: Date | string): string => {
    if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}/.test(date)) {
        return date.slice(0, 10);
    }
    const d = date instanceof Date ? date : new Date(date);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
};